import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractApiErrorMessage } from '../../shared/utils/extract-api-error-message.util';
import { ADMIN_INVOICES_API_PATHS } from '../constants/invoices-app-paths.constant';
import { Invoice } from '../types/invoice.type';
import { InvoiceStatus } from '../types/invoice-status.type';
import { InvoiceType } from '../types/invoice-type.type';

type ListInvoicesFilters = {
  segmentId?: string;
  organizationId?: string;
  type?: InvoiceType;
  status?: InvoiceStatus;
};

type ListInvoicesParams = ListInvoicesFilters & {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
};

type ListInvoicesResponse = {
  totalCount: number;
  invoices: Invoice[];
};

export function useListInvoices(params: ListInvoicesParams) {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<ListInvoicesResponse>({ totalCount: 0, invoices: [] });
  const [isLoading, setIsLoading] = useState(false);

  const fetchInvoices = useCallback(async () => {
    setIsLoading(true);
    try {
      const query = new URLSearchParams();
      if (params.segmentId) query.set('segmentId', params.segmentId);
      if (params.organizationId) query.set('organizationId', params.organizationId);
      if (params.type) query.set('type', params.type);
      if (params.status) query.set('status', params.status);
      query.set('page', String(params.page ?? 1));
      query.set('limit', String(params.limit ?? 10));
      query.set('sortBy', params.sortBy ?? 'createdAt');
      query.set('sortDirection', params.sortDirection ?? 'desc');

      const url = `${ADMIN_INVOICES_API_PATHS.LIST_INVOICES}?${query.toString()}`;
      const response = await HttpClient.get<ListInvoicesResponse>(url);
      setData(response);
    } catch (error) {
      enqueueSnackbar(
        extractApiErrorMessage(error, 'Erreur lors du chargement des factures'),
        { variant: 'error' },
      );
    } finally {
      setIsLoading(false);
    }
  }, [params.segmentId, params.organizationId, params.type, params.status, params.page, params.limit, params.sortBy, params.sortDirection, enqueueSnackbar]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  return { data, isLoading, refetch: fetchInvoices };
}
